import { useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { KpiPanelHistoryEntry } from './KpiPanelHistoryEntry';
import { HistoryEntry } from './historyTypes';
import { abyssColors } from '@/theme/colors';
import { useKpiHistoryData } from '../../hooks/useKpiHistoryData';
import { useKpiHistoryTransformations } from '../../hooks/useKpiHistoryTransformations';
import { KPI_NOTE_LOG_SHIFT_METADATA_KEY } from '../../constants';
import { ShiftType } from '@/__generated__/graphql';

export const KpiPanelHistory = () => {
  const { workpackVersionsData, logEntryVersionsData, commentsData, loading, error } =
    useKpiHistoryData();

  const { transformWorkpackVersion, transformLogEntryVersion } = useKpiHistoryTransformations();

  const historyEntries = useMemo(() => {
    // Process Workpack attachments versions
    const workpackEntries: HistoryEntry[] = (workpackVersionsData?.allVersions ?? [])
      .filter(
        (version) =>
          version.lastModifiedBy?.toLowerCase() !== 'cli' &&
          version.lastModifiedFor?.toLowerCase() !== 'system update'
      )
      .map((version) => transformWorkpackVersion(version))
      .filter((entry): entry is HistoryEntry => entry !== null);

    // Process LogEntry versions
    const logEntryEntries: HistoryEntry[] = (logEntryVersionsData?.allVersions ?? [])
      .filter(
        (version) =>
          version.lastModifiedBy?.toLowerCase() !== 'cli' &&
          version.lastModifiedFor?.toLowerCase() !== 'system update'
      )
      .map((version) => transformLogEntryVersion(version))
      .filter((entry): entry is HistoryEntry => entry !== null);

    const commentEntries: HistoryEntry[] = (commentsData?.allComments ?? []).map((comment) => {
      const shift = comment.metadata?.[KPI_NOTE_LOG_SHIFT_METADATA_KEY] as ShiftType | undefined;
      return {
        id: comment.id,
        timestamp: comment.createdAt,
        userName: comment.createdByDetails?.name || 'Unknown User',
        type: 'comment' as const,
        commentText: comment.text,
        commentKey: comment.key ?? undefined,
        shift: shift || null,
      };
    });

    return [...workpackEntries, ...logEntryEntries, ...commentEntries].sort(
      (a, b) => Number(b.timestamp) - Number(a.timestamp)
    );
  }, [
    workpackVersionsData?.allVersions,
    logEntryVersionsData?.allVersions,
    commentsData?.allComments,
    transformWorkpackVersion,
    transformLogEntryVersion,
  ]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load history. Please try again later.</Typography>
      </Box>
    );
  }

  if (historyEntries.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: abyssColors.primary[400] }}>
          No history available for this workpack.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} id="kpi-tracker-history-tab">
      {historyEntries.map((entry, index) => (
        <KpiPanelHistoryEntry
          key={`${entry.id}-${index}`}
          entry={entry}
          isLast={index === historyEntries.length - 1}
        />
      ))}
    </Box>
  );
};
